/**
 * @author       henry.pornchatrat@zumper.com (Henry Pornchatrat)
 * @copyright    Copyright (c) 2019, Zumper
 * @description  memoMapDispatchToProps util
 */

import { MapDispatchToPropsFunction } from 'react-redux'
import { Action, Dispatch } from 'redux'

import { createAllowedPropsAreEqual } from './createAllowedPropsAreEqual'

export type TAnyFunction = (...args: any[]) => any

export const memoMapDispatchToProps = <
  TDispatchProps extends Record<string, TAnyFunction>,
  Props extends object,
>(
  mapDispatchToProps: MapDispatchToPropsFunction<TDispatchProps, Props>,
  equalProps?: Array<keyof Props>,
  shallowEqualProps?: Array<keyof Props>
): MapDispatchToPropsFunction<TDispatchProps, Props> => {
  // bail early if the func does not use ownProps (react-redux already optimizes that case)
  if (mapDispatchToProps.length < 2) {
    return mapDispatchToProps
  }

  const areEqual = createAllowedPropsAreEqual(
    equalProps ?? [],
    shallowEqualProps
  )

  let prevMap: TDispatchProps
  let prevDispatch: Dispatch<Action>
  let prevOwnProps: Props
  return (dispatch: Dispatch<Action>, ownProps: Props) => {
    if (
      prevMap &&
      dispatch === prevDispatch &&
      areEqual(prevOwnProps, ownProps)
    ) {
      return prevMap
    }
    const map = mapDispatchToProps(dispatch, ownProps)
    prevMap = map
    prevDispatch = dispatch
    prevOwnProps = ownProps
    return map
  }
}
